import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { viewRewardCoupon, changeRewardCouponStatus } from "../../Redux/Actions/rewardCouponActions";
import toast from "react-hot-toast";
import MainLayout from "../../Layout/mainLayout";
import "./viewRewardCoupon.css";

const ViewRewardCoupon = () => {
    const { id } = useParams();
    const dispatch = useDispatch();

    const [couponData, setCouponData] = useState(null);
    const [pageLoading, setPageLoading] = useState(false);
    const [statusValue, setStatusValue] = useState("");
    const [statusUpdating, setStatusUpdating] = useState(false);

    const fetchDetails = async () => {
        try {
            setPageLoading(true);
            const res = await dispatch(viewRewardCoupon(id));
            if (res && res.statusCode === 200) {
                setCouponData(res.data);
                setStatusValue(res?.data?.status || "");
            } else {
                toast.error(res?.message || "Failed to load coupon details");
            }
        } catch {
            toast.error("Error fetching coupon details");
        } finally {
            setPageLoading(false);
        }
    };

    useEffect(() => {
        fetchDetails();
    }, [dispatch, id]); // eslint-disable-line

    const handleStatusUpdate = async () => {
        if (!statusValue) return toast.error("Please select status");
        try {
            setStatusUpdating(true);
            const res = await dispatch(changeRewardCouponStatus({ id, status: statusValue }));
            if (res?.statusCode === 200) {
                setCouponData((prev) => ({ ...prev, status: statusValue }));
                toast.success(res?.message || "Status updated successfully");
            } else {
                toast.error(res?.message || "Failed to update status");
            }
        } catch {
            toast.error("Something went wrong");
        } finally {
            setStatusUpdating(false);
        }
    };

    if (pageLoading) {
        return (
            <MainLayout>
                <div className="vrc-loader-wrap">
                    <div className="vrc-loader" />
                    <p className="vrc-loader-text">Loading coupon data…</p>
                </div>
            </MainLayout>
        );
    }

    if (!couponData) {
        return (
            <MainLayout>
                <div className="vrc-empty-wrap">
                    <div className="vrc-empty-icon">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M20 12V22H4V12" /><path d="M22 7H2v5h20V7z" /><path d="M12 22V7" />
                        </svg>
                    </div>
                    <p className="vrc-empty-text">No Coupon Data Found.</p>
                    <Link to="/dealsAndOffers" className="vrc-back-link">← Back to List</Link>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="vrc-wrapper">
                <div className="vrc-header">
                    <div className="vrc-header-left">
                        <Link to="/dealsAndOffers" className="vrc-back-btn">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="15 18 9 12 15 6" />
                            </svg>
                            Back
                        </Link>
                        <div className="vrc-header-divider" />
                        <div>
                            <div className="vrc-breadcrumb">Reward Coupon / List / View</div>
                            <h1 className="vrc-title">{couponData?.code || "No Data"}</h1>
                        </div>
                    </div>
                    <Link to={`/reward_coupon/edit/${id}`} className="vrc-edit-btn">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                        Edit Coupon
                    </Link>
                </div>

                <div className="vrc-hero-card">
                    <div className="vrc-hero-left">
                        <div className="vrc-hero-avatar">
                            {couponData?.code?.charAt(0)?.toUpperCase() || "R"}
                        </div>
                        <div>
                            <span className="vrc-hero-label">Coupon Code</span>
                            <span className="vrc-hero-name">{couponData?.code || "—"}</span>
                            {couponData?.agent?.name && (
                                <span className="vrc-hero-agent">Agent: {couponData.agent.name}</span>
                            )}
                        </div>
                    </div>
                    <div className="vrc-hero-meta">
                        {couponData?.status && (
                            <div className={`vrc-status-chip vrc-status-chip--${String(couponData.status).toLowerCase()}`}>
                                {couponData.status}
                            </div>
                        )}
                        <div className="vrc-hero-points">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                            </svg>
                            {couponData?.reward_point || 0} Points
                        </div>
                    </div>
                </div>

                <div className="vrc-grid-layout">
                    <div className="vrc-col">
                        <div className="vrc-card">
                            <div className="vrc-card-header">
                                <span className="vrc-card-badge">01</span>
                                <h2 className="vrc-card-title">Coupon Details</h2>
                            </div>
                            <div className="vrc-info-grid">
                                <div className="vrc-info-item">
                                    <span className="vrc-info-label">Coupon Code</span>
                                    <span className="vrc-info-value">{couponData?.code || "No Data"}</span>
                                </div>
                                <div className="vrc-info-item">
                                    <span className="vrc-info-label">No of Usage</span>
                                    <span className="vrc-info-value">{couponData?.no_of_usage ?? "No Data"}</span>
                                </div>
                                <div className="vrc-info-item">
                                    <span className="vrc-info-label">Minimum Price</span>
                                    <span className="vrc-info-value">{couponData?.min_price ? `₹${couponData.min_price}` : "No Data"}</span>
                                </div>
                                <div className="vrc-info-item">
                                    <span className="vrc-info-label">Reward Points</span>
                                    <span className="vrc-info-value">{couponData?.reward_point ?? "No Data"}</span>
                                </div>
                                <div className="vrc-info-item">
                                    <span className="vrc-info-label">Start Date</span>
                                    <span className="vrc-info-value">
                                        {couponData?.start_date ? new Date(couponData.start_date).toLocaleDateString() : "No Data"}
                                    </span>
                                </div>
                                <div className="vrc-info-item">
                                    <span className="vrc-info-label">End Date</span>
                                    <span className="vrc-info-value">
                                        {couponData?.end_date ? new Date(couponData.end_date).toLocaleDateString() : "No Data"}
                                    </span>
                                </div>
                                <div className="vrc-info-item">
                                    <span className="vrc-info-label">Status</span>
                                    <span className="vrc-info-value">{couponData?.status || "No Data"}</span>
                                </div>
                                <div className="vrc-info-item">
                                    <span className="vrc-info-label">Agent</span>
                                    <span className="vrc-info-value">{couponData?.agent?.name || "No Data"}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default ViewRewardCoupon;